import React from 'react';
import {
  COUNTRIES,
  getCourtSyncEligibilityMessage,
  isCountryEligibleForCourtSync,
  MANAGED_PAYMENTS_ELIGIBLE_COUNTRIES,
} from '@court-sync/shared';

export interface CountrySelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  error?: string;
  className?: string;
}

/**
 * Country dropdown with eligibility messaging for restricted countries.
 */
export function CountrySelectField({
  value,
  onChange,
  onBlur,
  name = 'country',
  id = 'country',
  error,
  className = 'input',
}: CountrySelectFieldProps) {
  const eligibilityMessage = value ? getCourtSyncEligibilityMessage(value) : null;
  const isRestricted = value ? !isCountryEligibleForCourtSync(value) : false;
  const countryOptions =
    value && isRestricted
      ? [
          COUNTRIES.find((c) => c.code === value.trim().toUpperCase()) ?? {
            code: value.trim().toUpperCase(),
            name: value.trim().toUpperCase(),
          },
          ...MANAGED_PAYMENTS_ELIGIBLE_COUNTRIES,
        ]
      : MANAGED_PAYMENTS_ELIGIBLE_COUNTRIES;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Country
      </label>
      <select
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={className}
        aria-label="Country"
        aria-invalid={isRestricted || !!error}
        aria-describedby={
          eligibilityMessage ? `${id}-eligibility` : error ? `${id}-error` : undefined
        }
      >
        <option value="">Select country</option>
        {countryOptions.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
      {eligibilityMessage && (
        <p
          id={`${id}-eligibility`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {eligibilityMessage}
        </p>
      )}
      {error && !eligibilityMessage && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export function isCountrySelectionBlocked(country: string): boolean {
  return Boolean(country && !isCountryEligibleForCourtSync(country));
}
